import {inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {VehicleTireRepo} from "../api/vehicle/vehicle-tire.repo";
import {DuckDbHelper, sqlDirection, sqlNumber, sqlSelectedIn, sqlText, sqlVehicleFilter} from "./duckdb.helper";
import {OrgDto} from "../dto/org.dto";
import {WrapperDto} from "../dto/wrapper.dto";
import {VehicleTireDto} from "../dto/vehicle/vehicle-tire.dto";
import {SearchVehicleTireRes} from "../dto/vehicle/search-vehicle-tire.res";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";
import {LoggingService} from "../../services/logger/logging.service";
import {isDefined} from "../../libs/utils/epic.utils";

@Injectable({providedIn: 'root'})
export class VehicleTireDuckDb extends VehicleTireRepo {

  private readonly duck: DuckDbHelper = inject(DuckDbHelper);
  private readonly log: LoggingService = inject(LoggingService);

  public override getById(_org: OrgDto, id: string, vehicleId: string): Observable<WrapperDto<VehicleTireDto>> {
    // Die Bezeichnung (205/55 R16 91V) setzt core beim Lesen zusammen (VehicleTireRepository); der Export hat nur die Teile.
    const select = `
      SELECT t.id               AS id,
             t.state            AS state,
             t.state_changed    AS stateChanged,
             t.manufacturer     AS manufacturer,
             t.width            AS width,
             t.height           AS height,
             t.identifier       AS identifier,
             t.diameter         AS diameter,
             t.load_index       AS loadIndex,
             t.speed_index      AS speedIndex,
             t.profile_depth    AS profileDepth,
             t.season           AS season,
             t.rim              AS rim,
             t.series           AS series,
             t.replace          AS replace,
             t.snow_chains      AS snowChains,
             t.storage_location AS storageLocation,
             t.accessories      AS accessories,
             t.vehicle_id       AS vehicleId,
             TRIM(CONCAT(t.width, '/', t.height, ' ', t.identifier, t.diameter, ' ', t.load_index, t.speed_index)) AS identification
      FROM vehicle_tire t
      WHERE t.id = ${sqlText(id)}
        AND t.vehicle_id = ${sqlText(vehicleId)}`;
    return this.duck.sql(select).pipe(map((res: any) =>
      DuckDbHelper.data('VehicleTireDto', new VehicleTireDto(DuckDbHelper.firstRes(res)))));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchVehicleTireRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const filter = this.buildFilter(request);
    const search = `
      SELECT t.id               AS id,
             t.state            AS state,
             t.state_changed    AS stateChanged,
             t.manufacturer     AS manufacturer,
             t.profile_depth    AS profileDepth,
             t.season           AS season,
             t.rim              AS rim,
             t.series           AS series,
             t.replace          AS replace,
             t.storage_location AS storageLocation,
             t.vehicle_id       AS vehicleId,
             v.contact_id       AS contactId,
             TRIM(CONCAT(t.width, '/', t.height, ' ', t.identifier, t.diameter, ' ', t.load_index, t.speed_index)) AS identification,
             COUNT(*)              OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING) AS total_results
      FROM vehicle_tire t
             LEFT JOIN vehicle v ON v.id = t.vehicle_id
      WHERE ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res: any) => {
      const rows: any[] = DuckDbHelper.hasRes(res) ? res : [];
      const result = new SearchVehicleTireRes({
        items: rows,
        hits: rows.length > 0 ? rows[0].total_results : 0,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchVehicleTireRes', result);
    }));
  }

  private buildSort(request: SearchRequest): string {
    let field;
    switch (request.sortField) {
      case 'state':
        field = 't.state';
        break;
      case 'identification':
        field = 't.width';
        break;
      case 'manufacturer':
        field = 't.manufacturer';
        break;
      case 'profileDepth':
        field = 't.profile_depth';
        break;
      case 'rim':
        field = 't.rim';
        break;
      case 'series':
        field = 't.series';
        break;
      case 'replace':
        field = 't.replace';
        break;
      default:
        this.log.warn('Unhandled sort field "{}"', [request.sortField]);
        field = 't.state';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildFilter(request: SearchRequest): string {
    return [
      sqlVehicleFilter(request, 't.vehicle_id'),
      sqlSelectedIn(request, 'state', 't.state'),
      sqlSelectedIn(request, 'rim', 't.rim'),
      sqlSelectedIn(request, 'series', 't.series'),
      sqlSelectedIn(request, 'replace', 't.replace')
    ].filter((f) => isDefined(f)).join(' AND ');
  }
}
